import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loading,
  SidePanelFormControl,
  TableControl,
} from 'spark-core-dx/components';
import { useCorePermissions, useGenericManager } from 'spark-core-dx/hooks';
import AIPanel from '../../components/AIChat/AIPanel';
import ConvokeCommandBar from '../../components/convoke/ConvokeCommandBar';
import DialogControl from '../../components/convoke/ConvokeDialogControl';
import ConvokeDialogMessage from '../../components/convoke/ConvokeDialogMessage';
import CardGridControl from '../resource/components/CardGridControl';
import ResourceStateControl from '../resource/components/ResourceStateControl';
import ClientCommandStatusTag from './ClientCommandStatusTag';
import ClientOnboardingScreen from './ClientOnboardingScreen';
import {
  buildOnboardingOperationRequest,
  buildOnboardingRunCards,
  canCancelOnboarding,
  CLIENT_ONBOARDING_ITEM_NAMES,
  CLIENT_ONBOARDING_NEW_RUN_CARD_KEY,
  CLIENT_ONBOARDING_OPERATIONS,
  CLIENT_ONBOARDING_RUN_CARD_METADATA,
  getOnboardingRunCardTitle,
} from './clientOnboardingContracts';
import {
  APPLICATION_OPTION_CARD_METADATA,
  CLIENT_COMMAND_ITEM_NAMES,
  CLIENT_COMMAND_OPERATIONS,
  CLIENT_COMMAND_PERMISSIONS,
  getFirstMetadata,
  getFirstResultItem,
  getResultMessage,
  isSuccessfulResult,
  normalizeSelectedOptionKeys,
  selectClientInformationFormFields,
} from './clientEntryContracts';
import {
  downloadClientEntraSetupScript,
  isMicrosoftEntraAuthentication,
} from './clientEntraSetupScriptContracts';
import { CLIENT_COMMAND_OPERATION_PERMISSIONS } from './clientCommandPermissions';

const runItemOperation = async (
  manager,
  operationName,
  item,
  fallbackMessage,
) => {
  if (!manager?.RunOperation) {
    throw new Error(fallbackMessage);
  }

  const result = await manager.RunOperation({
    operationName,
    item,
    isItem: true,
    isWarning: true,
    supressAllErrors: false,
  });
  if (!isSuccessfulResult(result)) {
    throw new Error(getResultMessage(result, fallbackMessage));
  }

  return getFirstResultItem(result);
};

const ScreenState = ({ loading, error, message }) => {
  if (loading) {
    return (
      <ResourceStateControl
        compact
        loading
        title="Loading Client Command data"
        loadingText="Loading the current screen."
      />
    );
  }

  if (error) {
    return (
      <ResourceStateControl
        compact
        tone="error"
        title="Client Command data unavailable"
        message={error}
      />
    );
  }

  if (message) {
    return (
      <div
        className="resource-workspace-access-note resource-workspace-access-note--success"
        role="status"
      >
        {message}
      </div>
    );
  }

  return null;
};

export const AllClientsScreen = ({
  screen,
  onboardingScreen,
  currentEnvironment,
  openOnboardingInitially = false,
}) => {
  const navigate = useNavigate();
  const workspaceMgr = useGenericManager(CLIENT_COMMAND_ITEM_NAMES.workspace);
  const summaryMgr = useGenericManager(CLIENT_COMMAND_ITEM_NAMES.summary);
  const onboardingMgr = useGenericManager(CLIENT_ONBOARDING_ITEM_NAMES.wizard);
  const workspaceMgrRef = useRef(workspaceMgr);
  const summaryMgrRef = useRef(summaryMgr);
  const onboardingMgrRef = useRef(onboardingMgr);
  const initialOnboardingPickerOpenedRef = useRef(false);
  const [workspace, setWorkspace] = useState(null);
  const [summaryMetadata, setSummaryMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [manageClientLoading, setManageClientLoading] = useState(false);
  const [manageClientError, setManageClientError] = useState('');
  const [onboardingPickerOpen, setOnboardingPickerOpen] = useState(false);
  const [onboardingPickerLoading, setOnboardingPickerLoading] =
    useState(false);
  const [onboardingPickerError, setOnboardingPickerError] = useState('');
  const [onboardingRunCards, setOnboardingRunCards] = useState([]);
  const [selectedOnboardingRunKeys, setSelectedOnboardingRunKeys] =
    useState([]);
  const [selectedOnboardingRunId, setSelectedOnboardingRunId] =
    useState(null);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [onboardingDraftToDelete, setOnboardingDraftToDelete] =
    useState(null);
  const [onboardingDraftDeleteLoading, setOnboardingDraftDeleteLoading] =
    useState(false);

  workspaceMgrRef.current = workspaceMgr;
  summaryMgrRef.current = summaryMgr;
  onboardingMgrRef.current = onboardingMgr;

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    setSelectedClient(null);
    setManageClientError('');
    try {
      const [result, metadataResult] = await Promise.all([
        workspaceMgrRef.current.RunOperation({
          operationName: CLIENT_COMMAND_OPERATIONS.loadClientsWorkspace,
          isItem: false,
          isWarning: true,
          supressAllErrors: false,
        }),
        summaryMgrRef.current.GetMetadata({}),
      ]);
      if (!isSuccessfulResult(result)) {
        throw new Error(getResultMessage(
          result,
          'The client inventory is unavailable.',
        ));
      }
      setWorkspace(getFirstResultItem(result));
      setSummaryMetadata(
        getFirstMetadata(metadataResult, summaryMgrRef.current),
      );
    } catch (ex) {
      setWorkspace(null);
      setError(ex?.message || 'The client inventory is unavailable.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [currentEnvironment, refresh]);

  const clients = Array.isArray(workspace?.Clients) ? workspace.Clients : [];
  const tableMetadata = useMemo(() => {
    const metadata = summaryMetadata
      ?? (typeof summaryMgr?.uiMetadata?.first === 'function'
        ? summaryMgr.uiMetadata.first()
        : summaryMgr?.uiMetadata?.[0]);
    if (!metadata) return summaryMgr?.uiMetadata;

    return [{
      ...metadata,
      DisplayName: '',
      Description: '',
      Icon: null,
    }];
  }, [summaryMetadata, summaryMgr]);

  const canOnboard = screen.CanExecute
    && onboardingScreen?.CanView !== false
    && onboardingScreen?.CanExecute !== false;
  const canManage = screen.CanExecute;
  const selectClient = useCallback((_id, row) => {
    setSelectedClient(row ?? null);
    setManageClientError('');
  }, []);
  const manageSelectedClient = useCallback(async () => {
    if (!canManage || !selectedClient || manageClientLoading) return;

    setManageClientLoading(true);
    setManageClientError('');
    try {
      const result = await summaryMgrRef.current.RunOperation({
        operationName: CLIENT_COMMAND_OPERATIONS.showClientCommandCenter,
        id: selectedClient._id,
        item: selectedClient,
        isItem: true,
        isWarning: true,
        supressAllErrors: false,
      });
      if (!isSuccessfulResult(result)) {
        throw new Error(getResultMessage(
          result,
          'The selected client could not be opened.',
        ));
      }
    } catch (ex) {
      setManageClientError(
        ex?.message || 'The selected client could not be opened.',
      );
    } finally {
      setManageClientLoading(false);
    }
  }, [canManage, manageClientLoading, selectedClient]);
  const openOnboardingPicker = useCallback(async () => {
    setOnboardingPickerOpen(true);
    setOnboardingPickerLoading(true);
    setOnboardingPickerError('');
    setSelectedOnboardingRunKeys([]);
    try {
      const wizard = await runItemOperation(
        onboardingMgrRef.current,
        CLIENT_ONBOARDING_OPERATIONS.load,
        {
          OnboardingRunId: null,
          CurrentEnvironment: currentEnvironment,
        },
        'Client onboarding is unavailable.',
      );
      setOnboardingRunCards(
        buildOnboardingRunCards(wizard?.AvailableRuns),
      );
    } catch (ex) {
      setOnboardingRunCards([]);
      setOnboardingPickerError(
        ex?.message || 'Client onboarding is unavailable.',
      );
    } finally {
      setOnboardingPickerLoading(false);
    }
  }, [currentEnvironment]);
  useEffect(() => {
    if (!openOnboardingInitially) {
      initialOnboardingPickerOpenedRef.current = false;
      return;
    }
    if (!canOnboard || initialOnboardingPickerOpenedRef.current) return;
    initialOnboardingPickerOpenedRef.current = true;
    openOnboardingPicker();
  }, [canOnboard, openOnboardingInitially, openOnboardingPicker]);
  const closeOnboardingPicker = useCallback(() => {
    setOnboardingPickerOpen(false);
    setSelectedOnboardingRunKeys([]);
    setOnboardingDraftToDelete(null);
    if (openOnboardingInitially) {
      navigate('/command/clients', { replace: true });
    }
  }, [navigate, openOnboardingInitially]);
  const requestDeleteOnboardingDraft = useCallback((run) => {
    if (!run || run.IsNewRun || !canCancelOnboarding(run)) return;
    setOnboardingDraftToDelete(run);
  }, []);
  const closeDeleteOnboardingDraft = useCallback(() => {
    if (onboardingDraftDeleteLoading) return;
    setOnboardingDraftToDelete(null);
  }, [onboardingDraftDeleteLoading]);
  const deleteOnboardingDraft = useCallback(async () => {
    if (!onboardingDraftToDelete || onboardingDraftDeleteLoading) {
      return undefined;
    }
    setOnboardingDraftDeleteLoading(true);
    try {
      const wizard = await runItemOperation(
        onboardingMgrRef.current,
        CLIENT_ONBOARDING_OPERATIONS.load,
        {
          OnboardingRunId: onboardingDraftToDelete.OnboardingRunId,
          CurrentEnvironment: currentEnvironment,
        },
        'The onboarding draft could not be loaded.',
      );
      const cancelledWizard = await runItemOperation(
        onboardingMgrRef.current,
        CLIENT_ONBOARDING_OPERATIONS.cancel,
        buildOnboardingOperationRequest({
          wizard,
          run: wizard?.Run,
          operationName: CLIENT_ONBOARDING_OPERATIONS.cancel,
          currentEnvironment,
        }),
        'The onboarding draft could not be deleted.',
      );
      setOnboardingRunCards(
        buildOnboardingRunCards(cancelledWizard?.AvailableRuns),
      );
      setSelectedOnboardingRunKeys((current) => current.filter(
        (key) => String(key) !== String(
          onboardingDraftToDelete.OnboardingRunId,
        ),
      ));
      setOnboardingDraftToDelete(null);
      return { Success: true, Items: [cancelledWizard] };
    } catch (ex) {
      return {
        Success: false,
        DisplayMessage:
          ex?.message || 'The onboarding draft could not be deleted.',
      };
    } finally {
      setOnboardingDraftDeleteLoading(false);
    }
  }, [
    currentEnvironment,
    onboardingDraftDeleteLoading,
    onboardingDraftToDelete,
  ]);
  const continueOnboarding = useCallback(() => {
    const selectedKey = selectedOnboardingRunKeys[0];
    if (!selectedKey) return;
    setSelectedOnboardingRunId(
      selectedKey === CLIENT_ONBOARDING_NEW_RUN_CARD_KEY
        ? null
        : selectedKey,
    );
    setOnboardingPickerOpen(false);
    setOnboardingOpen(true);
  }, [selectedOnboardingRunKeys]);
  const closeOnboarding = useCallback(() => {
    setOnboardingOpen(false);
    setSelectedOnboardingRunId(null);
    if (openOnboardingInitially) {
      navigate('/command/clients', { replace: true });
    }
  }, [navigate, openOnboardingInitially]);
  const completeOnboarding = useCallback(async (completedWizard) => {
    setOnboardingRunCards(
      buildOnboardingRunCards(completedWizard?.AvailableRuns),
    );
    setOnboardingOpen(false);
    setSelectedOnboardingRunId(null);
    await refresh();
    if (openOnboardingInitially) {
      navigate('/command/clients', { replace: true });
    }
  }, [navigate, openOnboardingInitially, refresh]);
  const commandBarActions = useMemo(() => [
    {
      key: 'refresh-clients',
      label: 'Refresh',
      iconKey: 'refresh',
      disabled: loading,
      onClick: refresh,
    },
    ...(canOnboard ? [{
      key: 'onboard-client',
      label: 'Onboard Client',
      iconKey: 'clientscreen',
      disabled: loading || onboardingPickerLoading,
      onClick: openOnboardingPicker,
    }] : []),
    {
      key: 'manage-client',
      label: 'Manage Client',
      iconKey: 'manage',
      disabled: !canManage
        || loading
        || manageClientLoading
        || !selectedClient,
      loading: manageClientLoading,
      onClick: manageSelectedClient,
    },
  ], [
    canManage,
    canOnboard,
    loading,
    manageClientLoading,
    manageSelectedClient,
    onboardingPickerLoading,
    openOnboardingPicker,
    refresh,
    selectedClient,
  ]);
  const onboardingCardHeaderActions = useMemo(() => [{
    key: 'delete-onboarding-draft',
    iconKey: 'delete',
    title: 'Delete onboarding draft',
    tone: 'danger',
    visible: (run) => (
      onboardingScreen?.CanWrite !== false
      && !run?.IsNewRun
      && canCancelOnboarding(run)
    ),
    disabled: onboardingDraftDeleteLoading,
    onClick: requestDeleteOnboardingDraft,
  }], [
    onboardingDraftDeleteLoading,
    onboardingScreen?.CanWrite,
    requestDeleteOnboardingDraft,
  ]);

  return (
    <>
      <main className="resource-workspace command-all-clients" aria-label="All Clients">
        <header className="resource-workspace-list-header">
          <div className="resource-workspace-list-header-title">
            <span className="resource-workspace-eyebrow">Command Center</span>
            <h1>All Clients</h1>
          </div>
        </header>
        <ConvokeCommandBar
          actions={commandBarActions}
          ariaLabel="Client actions"
          className="convoke-command-bar--screen"
          useSparkButtons
        />
        {manageClientError ? (
          <div
            className="resource-workspace-access-note resource-workspace-access-note--error"
            role="alert"
          >
            {manageClientError}
          </div>
        ) : null}
        <ScreenState loading={loading} error={error} />
        {!loading && !error ? (
          <TableControl
            data={clients}
            deepDataSync
            defaultNumRows={50}
            disableTableTitle
            enableMultiSelect={false}
            enableSingleSelect
            enableSidePanelEdit={false}
            filters={summaryMgr?.filters}
            loadingState={loading}
            metadata={tableMetadata}
            mgr={summaryMgr}
            onSelectedRowChange={selectClient}
            selectedRowId={selectedClient?._id ?? null}
          />
        ) : null}
      </main>
      <DialogControl
        openDialog={onboardingPickerOpen}
        title="Onboard Client"
        subTitle="Start a new client onboarding run or resume onboarding already in progress."
        okText="Continue"
        cancelText="Cancel"
        onCancel={closeOnboardingPicker}
        onX={closeOnboardingPicker}
        onOk={continueOnboarding}
        disableOk={
          onboardingPickerLoading
          || !!onboardingPickerError
          || selectedOnboardingRunKeys.length !== 1
        }
        loadingOk={onboardingPickerLoading}
      >
        <ConvokeDialogMessage
          message={onboardingPickerError}
          tone="error"
        />
        {onboardingPickerLoading ? (
          <div className="command-entry__loading" role="status">
            <Loading />
            <span>Loading client onboarding...</span>
          </div>
        ) : null}
        {!onboardingPickerLoading && !onboardingPickerError ? (
          <CardGridControl
            rows={onboardingRunCards}
            metadata={CLIENT_ONBOARDING_RUN_CARD_METADATA}
            cardFields={CLIENT_ONBOARDING_RUN_CARD_METADATA.Fields}
            cardTitleResolver={getOnboardingRunCardTitle}
            rowKey={(run) => run.OnboardingRunId}
            selectedRowKeys={selectedOnboardingRunKeys}
            onSelectedRowKeysChange={setSelectedOnboardingRunKeys}
            selectable
            activeFilterEnabled={false}
            disableInlineEdit
            disableCardClickEdit
            headerIconActions={onboardingCardHeaderActions}
            selectedHeaderIconActions={onboardingCardHeaderActions}
            emptyContent="No onboarding choices are available."
            minCardWidth="16rem"
            maxColumns={1}
          />
        ) : null}
      </DialogControl>
      <DialogControl
        openDialog={!!onboardingDraftToDelete}
        title="Delete Onboarding Draft"
        subTitle="The draft will be cancelled and removed from this list. Any records already created by the onboarding run are retained."
        okText="Delete"
        cancelText="Cancel"
        onCancel={closeDeleteOnboardingDraft}
        onX={closeDeleteOnboardingDraft}
        onOk={deleteOnboardingDraft}
        disableOk={onboardingDraftDeleteLoading}
        loadingOk={onboardingDraftDeleteLoading}
      />
      <AIPanel
        open={onboardingOpen}
        presentation="slideout"
        title="Onboard Client"
        className="ai-panel-floating-slideout"
        mapToClass="core-layout"
        width={620}
        minPanelWidth={620}
        panelStorageKey={null}
        chatEnabled={false}
        chatPaneToggleEnabled={false}
        singleSessionMode
        onCancel={closeOnboarding}
      >
        <ClientOnboardingScreen
          key={selectedOnboardingRunId || CLIENT_ONBOARDING_NEW_RUN_CARD_KEY}
          screen={onboardingScreen || screen}
          currentEnvironment={currentEnvironment}
          onboardingRunId={selectedOnboardingRunId}
          onCompleted={completeOnboarding}
        />
      </AIPanel>
    </>
  );
};

export const ClientCommandCenterScreen = ({
  screens,
  clientId,
  currentEnvironment,
  onHeaderChange,
}) => {
  const { canExecute } = useCorePermissions();
  const navigate = useNavigate();
  const manager = useGenericManager(CLIENT_COMMAND_ITEM_NAMES.commandCenter);
  const setupScriptManager = useGenericManager(
    CLIENT_COMMAND_ITEM_NAMES.entraSetupScript,
  );
  const managerRef = useRef(manager);
  const setupScriptManagerRef = useRef(setupScriptManager);
  const [center, setCenter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scriptBusy, setScriptBusy] = useState(false);
  const [scriptError, setScriptError] = useState('');
  const [scriptMessage, setScriptMessage] = useState('');
  managerRef.current = manager;
  setupScriptManagerRef.current = setupScriptManager;

  const execute = useCallback(async (operationName) => {
    setLoading(true);
    setError('');
    try {
      const result = await runItemOperation(
        managerRef.current,
        operationName,
        {
          ClientId: clientId,
          CurrentEnvironment: currentEnvironment,
        },
        'The Client Command Center is unavailable.',
      );
      setCenter(result);
    } catch (ex) {
      setCenter(null);
      setError(ex?.message || 'The Client Command Center is unavailable.');
    } finally {
      setLoading(false);
    }
  }, [clientId, currentEnvironment]);

  useEffect(() => {
    execute(CLIENT_COMMAND_OPERATIONS.loadClientCommandCenter);
  }, [execute]);

  const findScreen = (key) => screens.find((entry) => entry.Key === key);
  const informationScreen = findScreen('client-information');
  const activityScreen = findScreen('activity-logs');
  const authenticationScreen = findScreen('authentication');
  const metrics = (center?.Metrics || []).filter(
    (metric) => metric.IsAuthorized !== false,
  );
  const panels = (center?.Panels || []).filter(
    (panel) => (
      panel.IsAuthorized !== false
      && panel.Key !== 'client'
    ),
  );
  useEffect(() => {
    if (!center?.OfficialName) return;
    onHeaderChange?.({
      eyebrow: 'Overview',
      title: center.OfficialName,
    });
  }, [center?.OfficialName, onHeaderChange]);

  const generateEntraSetupScript = useCallback(async () => {
    setScriptBusy(true);
    setScriptError('');
    setScriptMessage('');
    try {
      const scriptItem = await runItemOperation(
        setupScriptManagerRef.current,
        CLIENT_COMMAND_OPERATIONS.generateClientEntraSetupScript,
        {
          ClientId: clientId,
          CurrentEnvironment: currentEnvironment,
        },
        'The Microsoft Entra setup script could not be generated.',
      );
      downloadClientEntraSetupScript(scriptItem);
      setScriptMessage(
        `Downloaded ${scriptItem.FileName}. Give this script to the client Microsoft Entra administrator.`,
      );
    } catch (ex) {
      setScriptError(
        ex?.message
        || 'The Microsoft Entra setup script could not be generated.',
      );
    } finally {
      setScriptBusy(false);
    }
  }, [clientId, currentEnvironment]);

  const commandBarActions = useMemo(() => [
    {
      key: 'refresh-client-overview',
      label: 'Refresh',
      iconKey: 'refresh',
      disabled: loading,
      onClick: () => execute(CLIENT_COMMAND_OPERATIONS.loadClientCommandCenter),
    },
    ...(informationScreen?.CanAccess ? [{
      key: 'open-client-information',
      label: 'Client Information',
      iconKey: 'view',
      disabled: loading,
      onClick: () => navigate(`/command/clients/${clientId}/information`),
    }] : []),
    ...(activityScreen?.CanAccess ? [{
      key: 'open-client-activity',
      label: 'View Activity',
      iconKey: 'view',
      disabled: loading,
      onClick: () => navigate(`/command/clients/${clientId}/activity`),
    }] : []),
    ...(
      canExecute(CLIENT_COMMAND_OPERATION_PERMISSIONS.generateClientEntraSetupScript)
      && authenticationScreen?.CanAccess
      && authenticationScreen?.CanView
      && isMicrosoftEntraAuthentication(
        center?.AuthenticationProvider,
      ) ? [{
          key: 'generate-client-entra-setup-script',
          label: 'Generate Entra Setup Script',
          iconKey: 'download',
          disabled: loading || scriptBusy,
          onClick: generateEntraSetupScript,
        }] : []
    ),
  ], [
    activityScreen?.CanAccess,
    authenticationScreen?.CanAccess,
    authenticationScreen?.CanView,
    canExecute,
    center?.AuthenticationProvider,
    clientId,
    execute,
    generateEntraSetupScript,
    informationScreen?.CanAccess,
    loading,
    navigate,
    scriptBusy,
  ]);

  return (
    <div className="resource-workspace-overview command-entry">
      <ConvokeCommandBar
        actions={commandBarActions}
        ariaLabel="Overview actions"
        className="convoke-command-bar--screen"
        useSparkButtons
      />
      <ScreenState loading={loading} error={error} />
      {scriptError ? (
        <div
          className="resource-wizard__alert"
          role="alert"
        >
          {scriptError}
        </div>
      ) : null}
      <ConvokeDialogMessage message={scriptMessage} />
      {!loading && center ? (
        <>
          {center.ResultState !== 'Ready' ? (
            <div
              className="resource-workspace-access-note resource-workspace-access-note--error"
              role="status"
            >
              <strong>{center.OverallReadiness}</strong>
              {center.ResultMessage ? ` — ${center.ResultMessage}` : null}
            </div>
          ) : null}
          <div className="resource-workspace-metric-grid" aria-label="Client readiness metrics">
            {metrics.map((metric) => (
              <div className="resource-workspace-metric" key={metric.Key}>
                <span>{metric.DisplayName}</span>
                <strong>{metric.Value}</strong>
              </div>
            ))}
          </div>
          {center.Blockers?.length ? (
            <section className="resource-workspace-panel" role="alert">
              <div className="resource-workspace-section-title resource-workspace-section-title--embedded">
                <h3>Open blockers</h3>
              </div>
              <div className="resource-workspace-record-list">
                {center.Blockers.map((blocker) => (
                  <article
                    className="resource-workspace-record-row resource-workspace-record-row--stacked"
                    key={blocker.Code}
                  >
                    <div>
                      <strong>{blocker.DisplayName}</strong>
                      <span>{blocker.Summary}</span>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ) : null}
          <div className="resource-workspace-two-column">
            {panels.map((panel) => (
              <section className="resource-workspace-panel" key={panel.Key}>
                <div className="resource-workspace-section-title resource-workspace-section-title--with-action">
                  <div>
                    <h2>{panel.DisplayName}</h2>
                    {panel.Message ? <p>{panel.Message}</p> : null}
                  </div>
                  {panel.Key !== 'configuration' ? (
                    <ClientCommandStatusTag status={panel.State} />
                  ) : null}
                </div>
                {panel.Rows?.length ? (
                  <div className="resource-workspace-record-list">
                    {panel.Rows.map((row) => (
                      <article
                        className="resource-workspace-record-row"
                        key={`${panel.Key}-${row.Name}-${row.Summary}`}
                      >
                        <div>
                          <strong>{row.Name}</strong>
                          <span>{row.Summary}</span>
                        </div>
                        <ClientCommandStatusTag status={row.Status} />
                      </article>
                    ))}
                  </div>
                ) : null}
              </section>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
};

const formatAddress = (address) => {
  if (!address) return 'Not provided';
  const preferredKeys = [
    'Address1',
    'Address2',
    'City',
    'State',
    'PostalCode',
    'Country',
  ];
  const values = preferredKeys
    .map((key) => address[key])
    .filter((value) => String(value ?? '').trim());
  return values.length ? values.join(', ') : 'Not provided';
};

const getSelectedApplicationOptionDisplayNames = (applicationOptions) => {
  const selectedKeys = new Set(
    normalizeSelectedOptionKeys(applicationOptions?.SelectedKeys)
      .map((key) => key.toLowerCase()),
  );

  return (applicationOptions?.Options || [])
    .filter((option) => selectedKeys.has(
      String(option?.Key || '').trim().toLowerCase(),
    ))
    .map((option) => String(option?.DisplayName || '').trim())
    .filter(Boolean);
};

const InformationGroup = ({ title, description, rows }) => (
  <section className="resource-workspace-panel">
    <div className="resource-workspace-section-title">
      <h2>{title}</h2>
      {description ? <p>{description}</p> : null}
    </div>
    <dl className="resource-workspace-facts">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>
            {value === null || value === undefined || value === ''
              ? 'Not provided'
              : value}
          </dd>
        </div>
      ))}
    </dl>
  </section>
);

export const ClientInformationScreen = ({
  clientId,
  currentEnvironment,
  onHeaderChange,
}) => {
  const { canExecute } = useCorePermissions();
  const informationMgr = useGenericManager(
    CLIENT_COMMAND_ITEM_NAMES.clientInformation,
  );
  const optionsMgr = useGenericManager(
    CLIENT_COMMAND_ITEM_NAMES.applicationOptions,
  );
  const informationMgrRef = useRef(informationMgr);
  const optionsMgrRef = useRef(optionsMgr);
  const canSaveClientInformation = canExecute(
    CLIENT_COMMAND_PERMISSIONS.saveClientInformation,
  );
  const canSetClientApplicationOptions = canExecute(
    CLIENT_COMMAND_PERMISSIONS.setClientApplicationOptions,
  );
  const [information, setInformation] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [options, setOptions] = useState(null);
  const [selectedOptionKeys, setSelectedOptionKeys] = useState([]);

  informationMgrRef.current = informationMgr;
  optionsMgrRef.current = optionsMgr;

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const [result, metadataResult] = await Promise.all([
        runItemOperation(
          informationMgrRef.current,
          CLIENT_COMMAND_OPERATIONS.loadClientInformation,
          {
            ClientId: clientId,
            CurrentEnvironment: currentEnvironment,
          },
          'Client Information is unavailable.',
        ),
        informationMgrRef.current.GetMetadata({ forceReload: true }),
      ]);
      setInformation(result);
      setOptions(result.ApplicationOptions || null);
      setSelectedOptionKeys(result.ApplicationOptions?.SelectedKeys || []);
      setMetadata(
        getFirstMetadata(metadataResult, informationMgrRef.current),
      );
    } catch (ex) {
      setInformation(null);
      setError(ex?.message || 'Client Information is unavailable.');
    } finally {
      setLoading(false);
    }
  }, [clientId, currentEnvironment]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!information?.Name) return;
    onHeaderChange?.({
      title: information.Name,
    });
  }, [information?.Name, onHeaderChange]);

  const formFields = useMemo(
    () => selectClientInformationFormFields(metadata),
    [metadata],
  );
  const selectedApplicationOptionDisplayNames = useMemo(
    () => getSelectedApplicationOptionDisplayNames(
      information?.ApplicationOptions,
    ),
    [information?.ApplicationOptions],
  );

  const saveInformation = async (updated) => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const saved = await runItemOperation(
        informationMgrRef.current,
        CLIENT_COMMAND_OPERATIONS.saveClientInformation,
        {
          ...updated,
          ClientId: clientId,
          CurrentEnvironment: currentEnvironment,
          ConcurrencyToken: information.ConcurrencyToken,
        },
        'Client Information changes were not saved.',
      );
      setInformation(saved);
      setOptions(saved.ApplicationOptions || null);
      setSelectedOptionKeys(saved.ApplicationOptions?.SelectedKeys || []);
      setEditOpen(false);
      setDraft(null);
      setMessage('Client Information changes are now available.');
      return { Success: true, Items: [saved] };
    } catch (ex) {
      return {
        Success: false,
        DisplayMessage:
          ex?.message || 'Client Information changes were not saved.',
      };
    } finally {
      setSaving(false);
    }
  };

  const openApplicationOptions = () => {
    setOptions(information?.ApplicationOptions || null);
    setSelectedOptionKeys(
      normalizeSelectedOptionKeys(
        information?.ApplicationOptions?.SelectedKeys,
      ),
    );
    setOptionsOpen(true);
  };

  const saveApplicationOptions = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const saved = await runItemOperation(
        optionsMgrRef.current,
        CLIENT_COMMAND_OPERATIONS.setClientApplicationOptions,
        {
          ...options,
          ClientId: clientId,
          CurrentEnvironment: currentEnvironment,
          ApplicationId: options.ApplicationId,
          ExpectedConfigurationVersion:
            options.ExpectedConfigurationVersion
            || options.ConfigurationVersion,
          SelectedKeys: normalizeSelectedOptionKeys(selectedOptionKeys),
        },
        'Application Options were not saved.',
      );
      setOptions(saved);
      setSelectedOptionKeys(saved.SelectedKeys || []);
      setInformation((current) => ({
        ...current,
        ApplicationOptions: saved,
        SelectedApplicationOptionCount: saved.SelectedKeys?.length || 0,
      }));
      setOptionsOpen(false);
      setMessage('Application Option selections are now available.');
      return { Success: true, Items: [saved] };
    } catch (ex) {
      const failureMessage =
        ex?.message || 'Application Options were not saved.';
      return {
        Success: false,
        DisplayMessage: failureMessage,
      };
    } finally {
      setSaving(false);
    }
  };

  const commandBarActions = [
    {
      key: 'refresh-client-information',
      label: 'Refresh',
      iconKey: 'refresh',
      disabled: loading || saving,
      onClick: refresh,
    },
    ...(canSaveClientInformation ? [{
      key: 'edit-client-information',
      label: 'Edit',
      iconKey: 'edit',
      disabled: !information || !metadata,
      onClick: () => {
        setDraft({ ...information });
        setEditOpen(true);
      },
    }] : []),
    ...(canSetClientApplicationOptions
      && information?.ApplicationOptions ? [{
        key: 'edit-client-application-options',
        label: 'Application Options',
        iconKey: 'checklist',
        disabled: !information,
        onClick: openApplicationOptions,
      }] : []),
  ];

  return (
    <div className="resource-workspace-overview command-entry">
      <ConvokeCommandBar
        actions={commandBarActions}
        ariaLabel="Client Information actions"
        className="convoke-command-bar--screen"
        useSparkButtons
      />
      <ScreenState loading={loading} error={error} message={message} />
      {!loading && information ? (
        <>
          <div className="resource-workspace-two-column">
            <InformationGroup
              title="Company"
              description="Identity and lifecycle information for the selected client."
              rows={[
                ['Official Name', information.Name],
                ['Display Name', information.DisplayName],
                ['Short Name', information.ShortName],
                ['Client Identifier', information.ClientIdentifier],
                ['EIN', information.EIN],
                ['Lifecycle', information.Lifecycle],
                ['Database Partition Key', information.PartitionKey],
              ]}
            />
            <InformationGroup
              title="Contact"
              description="Primary administrative contact information."
              rows={[
                ['First Name', information.FirstName],
                ['Last Name', information.LastName],
                ['Email', information.Email],
                ['Primary Phone', information.PrimaryPhone],
                ['Secondary Phone', information.SecondaryPhone],
              ]}
            />
            <InformationGroup
              title="Addresses"
              description="Primary and secondary business addresses."
              rows={[
                ['Primary Address', formatAddress(information.PrimaryAddress)],
                ['Secondary Address', formatAddress(information.SecondaryAddress)],
              ]}
            />
            <InformationGroup
              title="Application Options"
              description="Code-linked application behaviors selected for this client."
              rows={[
                [
                  'Selected Options',
                  information.SelectedApplicationOptionCount || 0,
                ],
                [
                  'Configuration',
                  selectedApplicationOptionDisplayNames.length
                    ? selectedApplicationOptionDisplayNames.join(', ')
                    : 'No options selected.',
                ],
              ]}
            />
          </div>
        </>
      ) : null}

      <SidePanelFormControl
        open={editOpen}
        title="Edit Client Information"
        metadata={metadata}
        formFields={formFields}
        itemData={draft}
        onSave={saveInformation}
        onCancel={() => setEditOpen(false)}
        onUpdateSidePanel={() => {}}
        saveButtonText="Save"
        setOpenEditSidePanel={setEditOpen}
        setItemData={setDraft}
        viewOnly={false}
      />

      <DialogControl
        openDialog={optionsOpen}
        title="Application Options"
        subTitle={options?.Message || 'Select the code-linked options for this client.'}
        okText="Save Options"
        cancelText="Cancel"
        onCancel={() => setOptionsOpen(false)}
        onX={() => setOptionsOpen(false)}
        onOk={saveApplicationOptions}
        disableOk={saving}
        loadingOk={saving}
        className="command-entry__options-dialog"
      >
        {options?.UnknownPersistedKeys?.length ? (
          <ConvokeDialogMessage
            message="Stored options contain unavailable keys. Reconcile the configuration version before saving."
            tone="error"
          />
        ) : null}
        <CardGridControl
          className="command-entry__options-card-grid"
          rows={options?.Options || []}
          metadata={APPLICATION_OPTION_CARD_METADATA}
          cardFields={APPLICATION_OPTION_CARD_METADATA.Fields}
          rowKey={(option) => option.Key}
          selectedRowKeys={selectedOptionKeys}
          onSelectedRowKeysChange={setSelectedOptionKeys}
          selectable
          multiSelect
          activeFilterEnabled={false}
          selectableRowDisabledResolver={(option) => option.IsSelectable === false}
          emptyContent="No Application Options are available."
          minCardWidth="16rem"
          maxColumns={Math.min(2, Math.max(1, options?.Options?.length || 1))}
        />
      </DialogControl>
    </div>
  );
};
